<template>
  <a
    :href="url"
    :class="b()"
  >
    <c-product-image
      :class="b('image')"
      :images="image ? [image] : []"
      :picture-sizes="pictureSizes"
      :text-image-overlay="textImageOverlay"
      :label="label"
    />
    <div :class="b('content-wrapper')">
      <div
        v-if="showFavoritesToggle"
        :class="b('add-to-favorites')"
      >
        <c-favorite-product-toggle :product="favoritesProduct" />
      </div>
      <h3
        v-if="heading"
        :class="b('heading')"
      >
        {{ heading }}
      </h3>
      <div
        v-if="referenceCode"
        :class="b('reference-code')"
      >
        {{ referenceCode }}
      </div>
      <p
        v-if="description"
        :class="b('description')"
      >
        {{ description }}
      </p>
      <span
        v-if="showReadMore"
        :class="b('read-more')"
      >
        {{ $t('c-teaser-tile.read-more') }}
        <e-icon
          icon="i-chevron--right"
          size="14"
        />
      </span>
    </div>
  </a>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue';
  import { PRODUCT_DISPLAY_PICTURE_SIZES } from '@/setup/globals';
  import cFavoriteProductToggle from '@/components/c-favorite-product-toggle.vue';
  import cProductImage from '@/components/c-product-image.vue';
  import eIcon from '@/elements/e-icon.vue';
  import { Image } from '@/types/image';
  import { FavoritesProduct, TextLabel } from '@/types/product';
  import { ImageSizes } from '@/types/sizes';

  // type Setup = {};

  type Data = {
    pictureSizes: ImageSizes;
  };

  /**
   * Shows a teaser tile link
   */
  export default defineComponent({
    name: 'c-teaser-tile',

    components: {
      cFavoriteProductToggle,
      cProductImage,
      eIcon,
    },

    props: {
      /**
       * The id of the item
       */
      id: {
        type: Number,
        required: true,
      },

      /**
       * The url the teaser tile links to
       */
      url: {
        type: String,
        required: true,
      },

      /**
       * The image to display
       */
      image: {
        type: Object as PropType<Image | null>,
        default: null,
      },

      /**
       * The heading to display
       */
      heading: {
        type: String as PropType<string | null>,
        default: null,
      },

      /**
       * The description to display
       */
      description: {
        type: String as PropType<string | null>,
        default: null,
      },

      /**
       * If to show the "Read More" span
       */
      showReadMore: {
        type: Boolean,
        default: false,
      },

      /**
       * Define the overlay text.
       */
      textImageOverlay: {
        type: String as PropType<string | null>,
        default: '',
      },

      /**
       * Define the overlay label.
       */
      label: {
        type: Object as PropType<TextLabel | null>,
        default: null,
      },

      /**
       * A reference code for the product eg. sku.
       */
      referenceCode: {
        type: String as PropType<string | null>,
        default: '',
      },

      /**
       * If to show the "Add to / Remove from My Favorites" Toggle
       */
      showFavoritesToggle: {
        type: Boolean,
        default: false,
      },
    },
    // emits: {},

    // setup(): Setup {},

    data(): Data {
      return {
        pictureSizes: PRODUCT_DISPLAY_PICTURE_SIZES,
      };
    },

    computed: {
      favoritesProduct(): FavoritesProduct {
        return {
          id: this.id,
          mainImage: this.image,
          name: this.heading ?? '',
          sku: this.referenceCode ?? '',
          url: this.url,
        };
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    // methods: {},
    // render() {},
  });
</script>

<style lang="scss">
  @use 'sass:map';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/variables';

  .c-teaser-tile {
    $this: &;

    @include mixins.hover-effect--default();

    z-index: 1;
    display: flex;
    align-items: center;
    flex-direction: column;
    border: 1px solid var(--theme-color-grayscale--900);
    height: 100%;

    &:hover {
      #{$this}__read-more {
        text-decoration: underline;
        text-underline-offset: 2px;
      }
    }

    &__image {
      padding: 2.8rem;
      width: 100%;
      aspect-ratio: 1; // Square by default.
    }

    &__content-wrapper {
      @include mixins.hyphens();

      position: relative;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      flex-grow: 1;
      background-color: var(--theme-color-grayscale--950);
      padding: 1.5rem;
    }

    &__add-to-favorites {
      z-index: 2;
      position: absolute;
      top: -2rem;
      right: 1.5rem;
    }

    &__heading {
      @include mixins.heading-h4;
    }

    &__description {
      @include mixins.line-clamp(2, 1.5);

      font-size: variables.$font-size--small;
    }

    &__reference-code {
      @include mixins.line-clamp(1, 1.5);

      font-size: variables.$font-size--small;
      color: var(--theme-color-primary--2);
    }

    &__read-more {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-top: auto;
      align-self: flex-start;
    }
  }
</style>
